
import React, { useState, useMemo } from 'react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Skeleton } from '@/components/ui/skeleton';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { AlertCircle, BarChart3, CalendarDays, PieChart } from 'lucide-react';
import { subDays } from 'date-fns';
import { useAnalyticsData } from './hooks/useAnalyticsData';
import { WeeklyHoursChart } from './WeeklyHoursChart';
import { ProjectDistributionChart } from './ProjectDistributionChart';
import { MonthlyProjectView } from './MonthlyProjectView';
import { FilterBar } from './FilterBar';
import { RevenueSummaryCards } from './RevenueSummaryCards';
import { DateRange } from '@/components/common/DateRangePicker';

export function Analytics() {
  const [activeTab, setActiveTab] = useState<string>("overview");
  const [selectedProjectId, setSelectedProjectId] = useState<string | "all">("all");
  const [showBillableOnly, setShowBillableOnly] = useState<boolean>(false);
  const [showTeamProjectsOnly, setShowTeamProjectsOnly] = useState<boolean>(false);
  const [dateRange, setDateRange] = useState<DateRange>({
    from: subDays(new Date(), 30),
    to: new Date()
  });
  const [appliedFilters, setAppliedFilters] = useState({
    selectedProjectId: "all" as string | "all",
    showBillableOnly: false,
    showTeamProjectsOnly: false,
    dateRange: {
      from: subDays(new Date(), 30),
      to: new Date()
    }
  });

  const {
    weeklyHoursData,
    projectHoursData,
    timeEntries,
    projects,
    revenueSummaryData,
    isLoading,
    hasTimeEntries,
    hasProjects,
    hasProjectData,
  } = useAnalyticsData(appliedFilters);

  const handleApplyFilters = () => {
    setAppliedFilters({
      selectedProjectId,
      showBillableOnly,
      showTeamProjectsOnly,
      dateRange
    });
  };

  const handleClearFilters = () => {
    const defaultRange = {
      from: subDays(new Date(), 30),
      to: new Date()
    };
    setSelectedProjectId("all");
    setShowBillableOnly(false);
    setShowTeamProjectsOnly(false);
    setDateRange(defaultRange);
    setAppliedFilters({
      selectedProjectId: "all",
      showBillableOnly: false,
      showTeamProjectsOnly: false,
      dateRange: defaultRange
    });
  };

  const hasActiveFilters = useMemo(() => {
    const defaultRange = {
      from: subDays(new Date(), 30),
      to: new Date()
    };
    
    return appliedFilters.selectedProjectId !== "all" || 
           appliedFilters.showBillableOnly ||
           appliedFilters.showTeamProjectsOnly ||
           appliedFilters.dateRange.from.getTime() !== defaultRange.from.getTime() ||
           appliedFilters.dateRange.to.getTime() !== defaultRange.to.getTime();
  }, [appliedFilters]);

  if (isLoading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-8 w-1/2" />
        <Skeleton className="h-20 w-full" />
        <Skeleton className="h-32 w-full" />
        <Skeleton className="h-96 w-full" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <FilterBar
        selectedProjectId={selectedProjectId}
        projects={projects}
        dateRange={dateRange}
        showBillableOnly={showBillableOnly}
        showTeamProjectsOnly={showTeamProjectsOnly}
        onProjectChange={setSelectedProjectId}
        onDateRangeChange={setDateRange}
        onBillableToggle={setShowBillableOnly}
        onTeamProjectsToggle={setShowTeamProjectsOnly}
        onClearFilters={handleClearFilters}
        onApplyFilters={handleApplyFilters}
        hasActiveFilters={hasActiveFilters}
      />

      {hasTimeEntries && (
        <RevenueSummaryCards data={revenueSummaryData} />
      )}

      <Tabs defaultValue={activeTab} value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="mb-4 grid grid-cols-2 md:grid-cols-2 lg:w-[400px]">
          <TabsTrigger value="overview" className="flex items-center gap-1">
            <BarChart3 className="h-4 w-4" />
            <span>Charts Overview</span>
          </TabsTrigger>
          <TabsTrigger value="monthlyProjects" className="flex items-center gap-1">
            <CalendarDays className="h-4 w-4" />
            <span>Monthly Projects</span>
          </TabsTrigger>
        </TabsList>
        
        {!hasTimeEntries && (
          <Alert className="my-4">
            <AlertCircle className="h-4 w-4" />
            <AlertTitle>No time entries found</AlertTitle>
            <AlertDescription>
              {hasActiveFilters 
                ? "No time entries found for the selected filters. Try adjusting your date range, project, or client selection."
                : "Add some time entries to see analytics data here."
              }
            </AlertDescription>
          </Alert>
        )}
        
        <TabsContent value="overview">
          <div className="grid gap-4 md:grid-cols-2">
            <WeeklyHoursChart 
              weeklyHoursData={weeklyHoursData}
              hasTimeEntries={hasTimeEntries}
            />
            
            <ProjectDistributionChart 
              projectHoursData={projectHoursData}
              hasProjectData={hasProjectData}
            />
          </div>
        </TabsContent>
        
        <TabsContent value="monthlyProjects">
          <MonthlyProjectView 
            timeEntries={timeEntries}
            projects={projects}
            hasTimeEntries={hasTimeEntries}
          />
        </TabsContent>
      </Tabs>
    </div>
  );
}
